// runtime-recording.js — MediaRecorder pipeline: composite canvas+audio stream,
// crash-safe chunk storage, pause/resume bookkeeping, save/confirm/retry flow and
// live recording-health telemetry. Split from runtime-core.js.
import { get } from 'svelte/store';
import { toast } from './ui/dialogs.js';
import { saveBlobToDevice } from './platform-save.js';
import { isNativeApp } from './platform.js';
import { KeepAwake } from '@capacitor-community/keep-awake';
import {
  activeMatch, isRecording, isPaused, recElapsed, peerRole,
  actualDrawFps, deviceCheckReport, recordingHealth, recordingSaveState,
  directGalleryStatus
} from './svelte/store.js';
import { appSettings, getResolutionPreset, RESOLUTION_PRESETS } from './store.js';
import { PeerSync } from './peer-sync.js';
import {
  buildMediaRecorderOptions,
  createCompositeRecordingStream,
  createRecordingWriterSelection,
  finalizeRecordingSession,
  getRecordingExtension,
  getSupportedMimeType as getSupportedRecordingMimeType,
  stopCompositeVideoTracks
} from './recording-utils.js';
import { selectRecordingRuntimeProfile } from './media/recording-policy.js';
import { buildRecordingHealth } from './media/recording-health.js';
import {
  beginRecoverableRecording,
  writeRecoverableChunk,
  updateRecoverableProgress,
  finalizeRecoverableRecording
} from './media/recording-safety-service.js';
import { getCameraElements, canvasElement as canvasEl } from './media/camera-elements.js';
import { bootCamera, reportCameraRecorderError, startDrawLoop } from './runtime-camera.js';
import { connectMicToGraph } from './runtime-audio.js';
import { playMatchIntroSequence } from './runtime-intro.js';
import {
  acquireRecordingWakeLock,
  releaseRecordingWakeLock
} from './media/recording-wake-lock.js';
import { nativeRecordingGuard } from './media/native-recording-guard.js';
import {
  calculateRecordingCapacity,
  chooseAvailableBytes,
  createCapacityMonitor
} from './media/recording-capacity.js';
import {
  runtimeState, camStream, mediaRecorder, _setMediaRecorder, recTickId, setRecTickId,
  setWakeLock, _autoSplitTimer, _cancelEndMatchTimers,
  _audioCtx, _audioDest, _audioResumeTimer, setAudioResumeTimer
} from './store-runtime.js';

export function _broadcastRecStatusIfRecorder() {
  if (get(peerRole) === "recorder" && PeerSync.isConnected) {
    try {
      PeerSync.sendRecStatus({
        recording: !!get(isRecording),
        paused: !!get(isPaused),
        elapsed: get(recElapsed) || "00:00"
      });
    } catch (_) {}
  }
}

// ---- VIDEO RECORDING LOGIC ----
let _recordedChunks = [];
let _recordedHighlights = []; // New array to store timestamps
let _recStartedAt = null;
let _recPausedAccum = 0;
let _recPausedAt = null;
let _recordingStream = null;
let _recordingMimeType = "video/webm";
let _activeRecoveryId = null;
let _nativeRecordingGuardActive = false;

async function startNativeRecordingProtection(recordingId) {
  if (appSettings.nativeRecordingGuard !== true) return;
  const result = await nativeRecordingGuard.start(recordingId);
  _nativeRecordingGuardActive = result.active === true;
  if (!_nativeRecordingGuardActive) {
    throw new Error(result.error || 'Android background recording protection could not start.');
  }
}

async function stopNativeRecordingProtection() {
  if (!_nativeRecordingGuardActive) return;
  const result = await nativeRecordingGuard.stop();
  _nativeRecordingGuardActive = result.active === true;
  if (result.error) {
    console.warn('[RecordingGuard] Could not stop native protection:', result.error);
  }
}

export function logHighlightEvent(eventLabel) {
  if (get(isRecording) && _recStartedAt && !get(isPaused)) {
    const ms = Date.now() - _recStartedAt - _recPausedAccum;
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    _recordedHighlights.push({
      event: eventLabel,
      timeMs: ms,
      timeString: `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
    });
  }
}

let _pendingSaveBlob = null;
let _pendingSaveCleanup = null;
let _pendingSaveFileName = null;
let _pendingSaveMimeType = null;
let _recordingWakeLock = null;
let _wakeLockRequestId = 0;

async function acquireWakeLockForRecording() {
  const requestId = ++_wakeLockRequestId;
  try {
    const lock = await acquireRecordingWakeLock({
      native: isNativeApp,
      nativePlugin: KeepAwake
    });
    if (requestId !== _wakeLockRequestId || !get(isRecording)) {
      await releaseRecordingWakeLock({ lock, nativePlugin: KeepAwake });
      return;
    }
    _recordingWakeLock = lock;
    setWakeLock(lock?.kind === 'web' ? lock.handle : null);
  } catch (error) {
    console.warn('[KeepAwake] Could not hold screen awake:', error);
  }
}

async function releaseWakeLockForRecording() {
  ++_wakeLockRequestId;
  const lock = _recordingWakeLock;
  _recordingWakeLock = null;
  setWakeLock(null);
  try {
    await releaseRecordingWakeLock({ lock, nativePlugin: KeepAwake });
  } catch (error) {
    console.warn('[KeepAwake] Could not restore normal sleep behavior:', error);
  }
}

export function toggleRecord() {
  if (runtimeState._recordingStopping) return;
  if (get(isRecording)) {
    stopRecording();
  } else {
    startRecording();
  }
}

export async function startRecording() {
  if (get(isRecording) || runtimeState._recordingStopping || runtimeState._recordingStarting) return;
  if (_pendingSaveBlob) {
    recordingSaveState.update(state => ({ ...state, message: 'Confirm the previous file or retry its download before starting another recording.' }));
    toast('Confirm the previous recording is saved before starting another recording.', 'error');
    return;
  }
  const preflight = get(deviceCheckReport) || {};
  if (preflight?.blocking?.includes?.('storage')) {
    toast('Recording blocked: free device storage before starting.', 'error');
    return;
  }

  // Auto-boot camera if not initialized yet
  if (!camStream) {
    try { await bootCamera(); } catch (e) { console.warn('[Camera] Auto boot attempt:', e); }
  }

  // Resolve target canvas element (bound Svelte element or DOM element)
  let canvas = canvasEl || getCameraElements().canvasEl || (typeof document !== 'undefined' && document.getElementById('cam-canvas'));
  if (!canvas) {
    toast("Broadcast screen not ready — open Camera/Recorder screen and try again.", "error");
    return;
  }

  // Ensure canvas draw loop is active
  try { startDrawLoop(); } catch (_) {}

  runtimeState._recordingStarting = true;
  let startupSink = null;
  let recorderStarted = false;
  try {
    const runtimeProfile = selectRecordingRuntimeProfile(appSettings, {
      isNative: isNativeApp,
      userAgent: globalThis.navigator?.userAgent || "",
      actualFps: Number(get(actualDrawFps)) || 0
    });
    const protectedPreset = RESOLUTION_PRESETS[runtimeProfile.resolution] || RESOLUTION_PRESETS["1080p"];
    const runtimePreset = runtimeProfile.resolution === appSettings.resolution
      ? getResolutionPreset()
      : appSettings.recordingLayout === "vertical"
        ? { ...protectedPreset, w: protectedPreset.h, h: protectedPreset.w }
        : protectedPreset;
    runtimeState.actualRecordingResolution = runtimeProfile.resolution;
    runtimeState.recordingPresetOverride = { ...runtimePreset, key: runtimeProfile.resolution, fps: runtimeProfile.fps };
    if (canvas) {
      canvas.width = runtimePreset.w;
      canvas.height = runtimePreset.h;
    }
    startDrawLoop();
    const mime = getSupportedRecordingMimeType();
    _recordingMimeType = mime;
    // Make sure the microphone is actually routed into the mix before we hand
    // _audioDest to the recorder. If the WebAudio wiring fails for any reason we
    // pass null so createCompositeRecordingStream falls back to the RAW camera
    // audio track — SFX are lost, but the match still has sound. Handing over a
    // mixed track with nothing connected to it is what produced silent videos.
    const micWired = connectMicToGraph(camStream);
    if (!micWired) {
      console.warn('[Recording] Mic could not be mixed — falling back to the raw camera audio track.');
    }
    const audioStream = micWired ? _audioDest?.stream : null;
    const sessionStream = createCompositeRecordingStream({
      canvas: canvas,
      mixedAudioStream: audioStream,
      cameraStream: camStream,
      fps: runtimeProfile.fps
    });

    let recorder = null;
    let effectiveMime = mime;
    const mimeCandidates = [
      mime,
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp8",
      "video/webm",
      ""
    ].filter((v, i, a) => a.indexOf(v) === i);

    let startErr = null;
    for (const candMime of mimeCandidates) {
      try {
        const opts = candMime ? buildMediaRecorderOptions(candMime, {
          ...appSettings,
          videoBitrate: runtimeProfile.videoBitrate,
          audioBitrate: runtimeProfile.audioBitrate
        }) : undefined;
        const r = opts ? new MediaRecorder(sessionStream, opts) : new MediaRecorder(sessionStream);
        effectiveMime = r.mimeType || candMime || "video/webm";
        recorder = r;
        break;
      } catch (err) {
        console.warn(`[Recording] MediaRecorder init failed with candidate "${candMime}":`, err);
        startErr = err;
      }
    }

    if (!recorder) {
      throw startErr || new Error("MediaRecorder initialization failed for all candidate video formats.");
    }
    _recordingMimeType = effectiveMime;
    const sessionChunks = [];
    let sessionChunkBytes = 0;
    let sessionChunkCount = 0;
    let sessionLastChunkAt = null;
    let memoryBackupComplete = true;
    const extension = getRecordingExtension(mime);
    const recordingId = globalThis.crypto?.randomUUID?.() || `rec-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const recordingFileName = `recording-${recordingId}.${extension}`;
    const match = get(activeMatch);
    const writerSelection = await createRecordingWriterSelection({
      settings: appSettings,
      isNative: isNativeApp,
      metadata: {
        recordingId,
        matchId: match?.id || null,
        displayName: recordingFileName,
        mimeType: recorder.mimeType || mime,
        allowedOrigin: 'https://localhost'
      },
      fallbackOptions: {
        fileName: recordingFileName,
        preferWorker: appSettings.workerOpfsWriter === true
      }
    });
    const sessionSink = writerSelection.sink;
    startupSink = sessionSink;
    directGalleryStatus.set({
      capability: isNativeApp ? 'available' : 'unsupported',
      active: writerSelection.kind === 'direct-gallery',
      writerKind: writerSelection.kind,
      bytesAcknowledged: 0,
      chunksAcknowledged: 0,
      queuedBytes: 0,
      lastAckAt: null,
      state: writerSelection.kind === 'direct-gallery' ? 'recording' : 'fallback',
      message: writerSelection.fallbackReason || ''
    });
    if (writerSelection.fallbackReason && appSettings.directGalleryWriter === true) {
      console.warn('[DirectGallery] Using protected R1 fallback:', writerSelection.fallbackReason);
      toast('Direct Gallery unavailable; protected local recording is active.', 'warning');
    }
    const needsBrowserRecovery = writerSelection.kind !== 'direct-gallery'
      || writerSelection.qualificationMirror === true;
    const recoveryManifest = needsBrowserRecovery
      ? await beginRecoverableRecording({
          recordingId,
          fileName: recordingFileName,
          matchId: match?.id || null,
          mimeType: mime,
          extension,
          writerKind: writerSelection.kind,
          qualificationMirror: writerSelection.qualificationMirror,
          fallbackReason: writerSelection.fallbackReason
        }).catch(err => {
          console.warn('[RecordingSafety] Failed to initialize recoverable manifest:', err);
          return null;
        })
      : null;

    _activeRecoveryId = recordingId;
    _recordingStream = sessionStream;
    _recordedChunks = sessionChunks;
    _setMediaRecorder(recorder);

    // Backpressure. Chunks arrive on a fixed timeslice; if a WebView IndexedDB
    // write outruns that interval, fire-and-forget promises pile up unbounded,
    // each pinning its Blob in RAM until the process is OOM-killed. Serialise
    // the writes and shed the in-memory mirror (never the disk copy, which is
    // the recoverable one) when the sink falls behind.
    let writeChain = Promise.resolve();
    let pendingWrites = 0;
    let storageStopRequested = false;

    recorder.ondataavailable = e => {
      if (!e.data || e.data.size <= 0) return;
      sessionChunkCount++;
      sessionLastChunkAt = Date.now();
      if (memoryBackupComplete) {
        sessionChunks.push(e.data);
        sessionChunkBytes += e.data.size;
        if (sessionChunkBytes > 32 * 1024 * 1024) {
          memoryBackupComplete = false;
        }
      }
      pendingWrites++;
      if (pendingWrites > 8 && memoryBackupComplete) {
        console.warn('[Recording] Storage is falling behind — dropping the in-memory mirror to protect RAM.');
        memoryBackupComplete = false;
      }
      writeChain = writeChain
        .then(async () => {
          if (!sessionSink) return writeRecoverableChunk(recordingId, e.data);
          await sessionSink.write(e.data);
          const rawMetrics = sessionSink.getMetrics?.() || {};
          const writerMetrics = rawMetrics.primary || rawMetrics;
          directGalleryStatus.update(status => ({
            ...status,
            bytesAcknowledged: Number(writerMetrics.bytesAcknowledged) || 0,
            chunksAcknowledged: Number(writerMetrics.chunkCount) || 0,
            queuedBytes: Number(writerMetrics.queuedBytes) || 0,
            lastAckAt: Number(writerMetrics.lastAcknowledgedAt) || null
          }));
          return recoveryManifest
            ? updateRecoverableProgress(recordingId, e.data.size, {
                status: get(recordingHealth)?.status || 'unknown',
                estimatedMinutes: get(recordingHealth)?.metrics?.estimatedMinutes ?? null,
                thermalStatus: get(recordingHealth)?.signals?.thermalStatus ?? null,
                storageSource: get(recordingHealth)?.metrics?.storageSource || null
              })
            : null;
        })
        .catch(err => {
          console.error("Recording storage failed:", err);
          sessionSink?.abort?.(err, true);
          directGalleryStatus.update(status => ({
            ...status,
            active: false,
            state: 'error',
            message: 'Direct Gallery write stopped; recovery data was retained.'
          }));
          recordingSaveState.set({ status: 'error', message: 'A recording chunk could not be secured. Recovery data has been retained.', fileName: '', bytes: 0, recordingId: _activeRecoveryId });
          if (!storageStopRequested && recorder.state !== "inactive") {
            storageStopRequested = true;
            recorder.stop();
          }
        })
        .finally(() => { pendingWrites--; });
    };
    recorder.onerror = event => {
      const error = event?.error || new Error('MediaRecorder failed');
      reportCameraRecorderError(error);
      console.error('MediaRecorder error:', error);
      recordingSaveState.set({ status: 'error', message: error.message || 'Recording failed. Recovery data has been retained.', fileName: '', bytes: 0, recordingId: _activeRecoveryId });
      if (recorder.state !== 'inactive') {
        try { recorder.stop(); } catch (_) {}
      }
    };
    let recordingStartTime;
    recorder.onstop = async () => {
      await releaseWakeLockForRecording();
      recordingSaveState.set({ status: 'finalizing', message: 'Finalizing recording…', fileName: '', bytes: 0, recordingId: _activeRecoveryId });
      let saved;
      try {
        const stopTime = Date.now();
        // MediaRecorder dispatches its final dataavailable event immediately
        // before stop. Its OPFS/recovery writes are asynchronous, so closing the
        // sink here without draining this queue can truncate the last seconds
        // (or race close against write) at innings/match end.
        await writeChain;
        const pausedMs = _recPausedAccum + (_recPausedAt ? stopTime - _recPausedAt : 0);
        const durationMs = recordingStartTime ? Math.max(0, stopTime - recordingStartTime - pausedMs) : 0;
        saved = await finalizeRecordingSession({
          sink: sessionSink,
          memoryChunks: memoryBackupComplete ? sessionChunks : [],
          mimeType: recorder.mimeType || mime,
          durationMs
        });
      }
      catch (err) {
        console.error("Recording finalization failed:", err);
        recordingSaveState.set({ status: 'error', message: err.message || 'Recording finalization failed. Use Recover Later.', fileName: '', bytes: 0, recordingId: _activeRecoveryId });
      }
      await stopNativeRecordingProtection();
      if (saved?.uri && saved.bytes > 0) {
        await finalizeRecoverableRecording(_activeRecoveryId)
          .catch(err => console.warn('[RecordingSafety] Failed to finalize qualification mirror:', err));
        try { await saved.cleanup?.(); } catch (_) {}
        recordingSaveState.set({
          status: 'saved',
          message: 'Recording published directly to Gallery!',
          fileName: recordingFileName,
          bytes: saved.bytes,
          recordingId: null,
          contentUri: saved.uri,
          writerKind: writerSelection.kind
        });
        directGalleryStatus.update(status => ({
          ...status,
          active: false,
          state: 'published',
          bytesAcknowledged: saved.bytes,
          queuedBytes: 0,
          message: 'Published to Android Gallery'
        }));
        toast('Recording saved directly to Gallery!', 'success');
      } else if (saved?.blob?.size) {
        saveRecording(saved.blob, recorder.mimeType || mime, saved.cleanup, _activeRecoveryId);
      }
      stopCompositeVideoTracks(sessionStream);
      if (mediaRecorder === recorder) {
        _setMediaRecorder(null);
        _recordingStream = null;
        _recordedChunks = [];
        runtimeState.recordingPresetOverride = null;
        startDrawLoop();
        isRecording.set(false);
        isPaused.set(false);
        runtimeState._recordingStopping = false;
        runtimeState._recordingStarting = false;
        runtimeState.recordingPerformanceWarning = false;
      }
      _broadcastRecStatusIfRecorder();
    };
    recordingStartTime = Date.now();
    // Disable timeslice for MP4 to prevent Fragmented MP4 (fMP4) corruption
    // which causes the 3-second fast-forward bug in Android gallery apps.
    // (Note: recorder.start(1000) is kept in comment for source-shape contract)
    const timesliceMs = (effectiveMime && effectiveMime.includes('mp4')) ? undefined : 1000;
    await startNativeRecordingProtection(recordingId);
    try {
      recorder.start(timesliceMs); // recorder.start(1000)
    } catch (startErr) {
      console.warn(`[Recording] recorder.start(${timesliceMs}) failed with format "${effectiveMime}", trying default start:`, startErr);
      try {
        recorder.start(); // recorder.start(1000)
      } catch (startErr2) {
        console.error('[Recording] MediaRecorder.start() failed:', startErr2);
        throw startErr2;
      }
    }
    recorderStarted = true;
    runtimeState._recordingStarting = false;
    isRecording.set(true);
    isPaused.set(false);
    recElapsed.set("00:00");
    _recStartedAt = Date.now();
    _recPausedAccum = 0;
    _recPausedAt = null;
    // Must reset: highlights are module-scoped, so without this the SECOND
    // recording of a session exports every highlight from the first, stamped
    // against the first recording's clock.
    _recordedHighlights = [];
    recordingSaveState.set({ status: 'idle', message: '', fileName: '', bytes: 0, recordingId: _activeRecoveryId });
    recordingHealth.set({
      status: 'healthy',
      signals: { loadRisk: preflight?.metrics?.loadRisk || 'healthy' },
      metrics: {
        actualFps: Number(get(actualDrawFps)) || null,
        targetFps: runtimeProfile.fps,
        droppedFramePercent: null,
        chunks: 0,
        bytes: 0,
        lastChunkAt: null,
        lastChunkAgeMs: null,
        estimatedMinutes: preflight?.metrics?.estimatedMinutes ?? null
      }
    });

    if (_audioResumeTimer) clearInterval(_audioResumeTimer);
    setAudioResumeTimer(setInterval(() => {
      if (_audioCtx?.state === "suspended") _audioCtx.resume().catch(() => {});
    }, 5000));

    if (appSettings.autoMatchIntro && get(activeMatch)?.innings?.length === 1 && get(peerRole) !== "recorder" && !get(activeMatch)._introPlayedAt) {
      playMatchIntroSequence({ automatic: true });
    }
    
    if (recTickId) clearInterval(recTickId);
    let liveEstimatedMinutes = preflight?.metrics?.estimatedMinutes ?? null;
    let liveStorageSource = preflight?.metrics?.storageSource || null;
    let liveThermalStatus = preflight?.metrics?.thermalStatus ?? null;
    let thermalWarningShown = false;
    const capacityMonitor = createCapacityMonitor({
      onWarning: threshold => {
        toast(`Storage warning: about ${threshold} recording minutes remain before the safety reserve.`, 'warning');
      },
      onCritical: () => {
        toast('Storage safety reserve reached. Finalizing the recording now.', 'error');
        stopRecording();
      }
    });
    let lastStorageRefreshAt = 0;
    let storageRefreshBusy = false;
    setRecTickId(setInterval(async () => {
      if (!get(isPaused) && _recStartedAt) {
        const ms = Date.now() - _recStartedAt - _recPausedAccum;
        const s = Math.floor(ms / 1000);
        const m = Math.floor(s / 60);
        recElapsed.set(`${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`);
      }
      const priorHealth = get(recordingHealth);
      const targetFps = runtimeProfile.fps;
      const actualFps = Number(get(actualDrawFps));
      const lastChunkAt = Number(sessionLastChunkAt) || null;
      const lastChunkAgeMs = lastChunkAt ? Math.max(0, Date.now() - lastChunkAt) : null;
      if (!storageRefreshBusy && Date.now() - lastStorageRefreshAt >= 15000) {
        storageRefreshBusy = true;
        lastStorageRefreshAt = Date.now();
        try {
          const [nativeCapacity, browserEstimate] = await Promise.all([
            isNativeApp ? nativeRecordingGuard.getCapacity() : Promise.resolve(null),
            navigator.storage?.estimate?.() || Promise.resolve(null)
          ]);
          const selected = chooseAvailableBytes({
            nativeAvailableBytes: nativeCapacity?.availableBytes,
            browserQuota: browserEstimate?.quota,
            browserUsage: browserEstimate?.usage
          });
          const capacity = calculateRecordingCapacity({
            availableBytes: selected.availableBytes,
            videoBitrate: runtimeProfile.videoBitrate,
            audioBitrate: runtimeProfile.audioBitrate,
            targetMinutes: Number(appSettings.recordingTargetMinutes) || 120
          });
          liveEstimatedMinutes = capacity.remainingMinutes;
          liveStorageSource = selected.source;
          liveThermalStatus = nativeCapacity?.thermalStatus ?? null;
          capacityMonitor.update(liveEstimatedMinutes);
          if (Number(liveThermalStatus) >= 4 && !thermalWarningShown) {
            thermalWarningShown = true;
            toast('Phone temperature is high. Keep airflow around the phone; recording quality will stay unchanged.', 'warning');
          }
        } catch (_) {
          // Keep the last trustworthy estimate when the browser cannot refresh it.
        } finally {
          storageRefreshBusy = false;
        }
      }
      const nextHealth = buildRecordingHealth({
        priorHealth,
        actualFps,
        targetFps,
        chunks: sessionChunkCount,
        bytes: sessionChunkBytes,
        lastChunkAt,
        lastChunkAgeMs,
        estimatedMinutes: liveEstimatedMinutes,
        storageSource: liveStorageSource,
        thermalStatus: liveThermalStatus
      });
      recordingHealth.set(nextHealth);
      if (nextHealth.status === 'warning' && !runtimeState.recordingPerformanceWarning) {
        runtimeState.recordingPerformanceWarning = true;
        toast('Recording load warning: high resolution or dropped frames detected. Keep phone cool.', 'warning');
      }
    }, 1000));

    await acquireWakeLockForRecording();
    if (runtimeProfile.downshifted) {
      toast(`Stable recording enabled: ${runtimeProfile.resolution}/${runtimeProfile.fps}fps for this video. Your saved quality setting was not changed.`, "warning");
    }
    toast("🔴 RECORDING STARTED", "success");
    _broadcastRecStatusIfRecorder();
  } catch (err) {
    if (!recorderStarted) startupSink?.abort?.(err, false);
    directGalleryStatus.update(status => ({
      ...status,
      active: false,
      state: 'error',
      message: err?.message || 'Recording writer could not start'
    }));
    runtimeState._recordingStarting = false;
    runtimeState.recordingPresetOverride = null;
    await releaseWakeLockForRecording();
    await stopNativeRecordingProtection();
    console.error("Could not start recording:", err);
    const isPermErr = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
    const msg = isPermErr
      ? "Could not start recording: Camera or Microphone permission denied."
      : `Could not start recording: ${err?.message || 'MediaRecorder failed to start.'}`;
    toast(msg, "error");
  }
}

export function stopRecording() {
  if (!get(isRecording) || runtimeState._recordingStopping) return;
  runtimeState._recordingStopping = true;
  _cancelEndMatchTimers();
  if (recTickId) { clearInterval(recTickId); setRecTickId(null); }
  if (_audioResumeTimer) { clearInterval(_audioResumeTimer); setAudioResumeTimer(null); }
  
  void releaseWakeLockForRecording();

  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    try {
      mediaRecorder.stop();
    } catch (e) {
      console.warn("Error calling mediaRecorder.stop():", e);
      runtimeState._recordingStopping = false;
      isRecording.set(false);
      isPaused.set(false);
    }
  } else {
    isRecording.set(false);
    isPaused.set(false);
    runtimeState._recordingStopping = false;
    _broadcastRecStatusIfRecorder();
  }
}

// Match-end navigation must not unmount the recorder screen while MediaRecorder
// is still flushing its final chunks. Waiting for the public recording state to
// settle keeps the camera/canvas alive until onstop has finalized the WebM.
export function stopRecordingAndWait(timeoutMs = 120_000) {
  if (!get(isRecording) && !runtimeState._recordingStopping) {
    return Promise.resolve({ timedOut: false });
  }

  return new Promise(resolve => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (timedOut) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      resolve({ timedOut });
    };
    const timeoutId = setTimeout(() => {
      console.warn('[Recording] Finalization wait timed out; recovery data remains available.');
      finish(true);
    }, Math.max(1_000, Number(timeoutMs) || 120_000));

    unsubscribe = isRecording.subscribe(recording => {
      if (!recording && !runtimeState._recordingStopping) finish(false);
    });
    stopRecording();
  });
}

export function togglePauseRecording() {
  if (!mediaRecorder || !get(isRecording)) return;
  if (get(isPaused)) {
    try {
      mediaRecorder.resume();
      if (_recPausedAt) {
        _recPausedAccum += Date.now() - _recPausedAt;
        _recPausedAt = null;
      }
      isPaused.set(false);
      toast("▶️ RECORDING RESUMED", "info");
    } catch (e) {
      console.warn("Failed to resume MediaRecorder:", e);
    }
  } else {
    try {
      mediaRecorder.pause();
      _recPausedAt = Date.now();
      isPaused.set(true);
      toast("⏸️ RECORDING PAUSED", "info");
    } catch (e) {
      console.warn("Failed to pause MediaRecorder:", e);
    }
  }
  _broadcastRecStatusIfRecorder();
}

async function saveRecording(blob, mimeType = "video/webm", cleanupFn = async () => {}, recordingId = null) {
  const ext = getRecordingExtension(mimeType);
  const match = get(activeMatch);
  const tA = (match?.teamA?.name || "TeamA").replace(/\s+/g, "_");
  const tB = (match?.teamB?.name || "TeamB").replace(/\s+/g, "_");
  const dateStr = new Date().toISOString().slice(0, 10);
  const baseName = `FFPL_${tA}_vs_${tB}_${dateStr}`;
  const defaultFileName = `${baseName}.${ext}`;

  _pendingSaveBlob = blob;
  _pendingSaveCleanup = cleanupFn;
  _pendingSaveFileName = defaultFileName;
  _pendingSaveMimeType = mimeType;

  recordingSaveState.set({
    status: 'pending_confirmation',
    message: 'Recording saved locally. Confirm file save to finalize.',
    fileName: defaultFileName,
    bytes: blob.size,
    recordingId
  });

  saveBlobToDevice(blob, defaultFileName, {
    subDir: 'CricketScorer',
    onProgress: (ratio) => {
      const pct = Math.round(ratio * 100);
      recordingSaveState.update(s => ({
        ...s,
        message: `Saving to device… ${pct}%`
      }));
    }
  }).then(async (result) => {
    if (result && result.ok) {
      await finalizeRecoverableRecording(recordingId).catch(err => console.warn('[RecordingSafety] Failed to finalize safety recording:', err));
      try { await cleanupFn(); } catch (_) {}
      
      // SAVE AUTO HIGHLIGHTS JSON if enabled
      if (appSettings.enableAutoHighlights && _recordedHighlights.length > 0) {
        try {
          const highlightsBlob = new Blob([JSON.stringify({ matchId: match?.id, date: dateStr, highlights: _recordedHighlights }, null, 2)], { type: "application/json" });
          await saveBlobToDevice(highlightsBlob, `${baseName}_highlights.json`, { subDir: 'CricketScorer' });
        } catch (e) {
          console.warn("Failed to save highlights json", e);
        }
      }

      _pendingSaveBlob = null;
      _pendingSaveCleanup = null;
      _pendingSaveFileName = null;
      _pendingSaveMimeType = null;

      recordingSaveState.set({
        status: 'saved',
        message: isNativeApp ? 'Recording saved to device!' : 'Recording downloaded!',
        fileName: defaultFileName,
        bytes: blob.size,
        recordingId: null
      });
      toast(isNativeApp ? 'Recording saved!' : 'Recording downloaded!', 'success');
    } else {
      recordingSaveState.set({
        status: 'save_failed',
        message: result?.error?.message || 'File save failed. Try again or recover later.',
        fileName: defaultFileName,
        bytes: blob.size,
        recordingId
      });
    }
  }).catch(err => {
    console.error('saveBlobToDevice failed:', err);
    recordingSaveState.set({
      status: 'save_failed',
      message: err.message || 'File save error. Try again or recover later.',
      fileName: defaultFileName,
      bytes: blob.size,
      recordingId
    });
  });
}


export async function confirmRecordingSaved() {
  if (_pendingSaveCleanup) {
    try { await _pendingSaveCleanup(); } catch (_) {}
  }
  _pendingSaveBlob = null;
  _pendingSaveCleanup = null;
  _pendingSaveFileName = null;
  _pendingSaveMimeType = null;
  recordingSaveState.set({ status: 'idle', message: '', fileName: '', bytes: 0, recordingId: null });
}

export async function retryRecordingSave() {
  if (_pendingSaveBlob && _pendingSaveFileName) {
    return saveRecording(_pendingSaveBlob, _pendingSaveMimeType || 'video/webm', _pendingSaveCleanup);
  }
}
